import { createMemo, createSignal, For, useContext } from 'solid-js';
import clsx from 'clsx';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { Grid } from '../../grid-system/grid/grid';
import { BlogNewsListCardData, Category } from '../blog-news/blog-news';
import { EditorialCard } from '../editorial-card/editorial-card';
import { Section } from '../../grid-system/section/section';
import { useLocation, useNavigate, useSearchParams } from '@solidjs/router';
import { ErrorCatcher } from '../../tools/error-catcher';
import { ItemListSchema, mapBlogNewsToItemList } from '../structured-data';
import decodeHtmlEntities from '../../tools/decode-html-entities';
import { BlogsNewsComponentProps, TopCategory } from './blogs-news-types';
import { Tabnav, TabnavItem } from '../../ui-components/tabnav/tabnav';

export const gridSettings = {
    container: {
        templateShorthand: [4, 4, 4],
    },
};

const extractCategories = (accCategories: TopCategory[], categories?: Category[]) => {
    if (!categories) {
        return accCategories;
    }

    let uniqueCategories = [...accCategories];
    
    categories.forEach((category) => {            
        if (!uniqueCategories.find((uc) => uc.slug === category.slug)) {
            uniqueCategories.push({
                slug: category.slug,
                name: category.name,
            });
        }

        if (category?.children && category?.children.length > 0) {
            uniqueCategories = [...extractCategories(uniqueCategories, category.children)];
        }
    });

    return uniqueCategories;
};

const filterByAudience = (post: BlogNewsListCardData, audience?: string) => {
    switch (audience) {
        case 'all': return true;
        case 'patients': return !post?.isHealthcareProfessional;
        case 'hcp': return post?.isHealthcareProfessional;
        default: return true;
    };
};

export const extractTopCategories = (posts: BlogNewsListCardData[], audience?: string) => {
    const allCategories = posts
        .filter((post) => filterByAudience(post, audience))
        .reduce((accCategories: TopCategory[], post) => {
            accCategories = [...extractCategories(accCategories, post.categories)];

            return accCategories;
        }, []);

    return allCategories;
};

export const createGroupedBlogAndNewsCard = (props: { post: BlogNewsListCardData, largeCard?: boolean}) => {
    return createBlogAndNewsCard({
        post: props.post,
        inGrouping: true,
        largeCard: props.largeCard,
    });
};

export const createBlogAndNewsCard = (props: { post: BlogNewsListCardData, inGrouping?: boolean, largeCard?: boolean }) => {
    const { localize } = useContext(AppContext);

    const categoryLabel = props.post?.categories?.length
        ? props.post.categories[0].name
        : localize('blog', 'Blog');

    return <EditorialCard
        data={props.post}
        categoryLabel={categoryLabel}
    />;
};

export const BlogsNewsComponent = (props: BlogsNewsComponentProps) => {
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { localize, siteInfo } = useContext(AppContext);
    const [topCategories, setTopCategories] = createSignal<TopCategory[]>([]);

    const initialSlug = (searchParams.category ?? '').trim() || null;
    const [selectedSlug, setSelectedSlug] = createSignal<string | null>(initialSlug);

    const updateUrl = (slug: string | null) => {
        const query = slug ? `?category=${slug}` : '';
        navigate(`${location.pathname}${query}`, { scroll: false });
    };

    const selectCategory = (slug: string | null) => {
        setSelectedSlug(slug);
        updateUrl(slug);
    };

    const postMatchesSelected = (categories?: Category[]): boolean => {
        if (!categories) return false;
        const selected = selectedSlug();
        if (!selected) return true;
        return categories.some(
            (category) =>
                category.slug === selected ||
                postMatchesSelected(category.children),
        );
    };

    const filteredPosts = () => props.posts
        .filter((post) => filterByAudience(post, props.audience))
        .filter((post) => !selectedSlug() || postMatchesSelected(post.categories));

    const extractedTopCategories = extractTopCategories(props.posts, props.audience);
    setTopCategories(extractedTopCategories);

    const tabnavItems = createMemo<TabnavItem[]>(() => [
        { key: 'all', label: localize('all', 'All') },
        ...topCategories().map((c) => ({ key: c.slug, label: decodeHtmlEntities(c.name) })),
    ]);
    const activeTabKey = () => selectedSlug() ?? 'all';
    const handleTabChange = (key: string) => {
        selectCategory(key === 'all' ? null : key);
    };

    const itemListItems = () => mapBlogNewsToItemList(filteredPosts() as any);

    return (
        <ErrorCatcher componentName='Blog and news component'>
            <ItemListSchema
                items={itemListItems()}
                listOrder="ItemListOrderDescending"
                name={localize('blog-and-news', 'Blog and News')}
            />
            <Section templateShorthand={[12]} widthType='bgFull' customCss='padding-top: 0; padding-bottom: var(--spacing-cvi-section-default);'>
                <div class={clsx(
                    'flex flex-col gap-y-cvi-13 tablet-original:gap-y-cvi-section-close [--card-row-gap:var(--spacing-cvi-8)] tablet-original:[--card-row-gap:var(--spacing-cvi-11)]',
                    siteInfo.siteType === 'atos-care' && 'pt-cvi-10 tablet-original:pt-cvi-12',
                )}>
                    <div class="tablet-original:hidden">
                        <Tabnav
                            tabs={tabnavItems()}
                            activeKey={activeTabKey()}
                            onChange={handleTabChange}
                            ariaLabel={localize('filter-by-category', 'Filter by category')}
                            overflowAriaLabel={localize('more-categories', 'More categories')}
                        />
                    </div>
                    <div
                        class="hidden tablet-original:flex items-start gap-x-cvi-8"
                        role="tablist"
                        aria-label={localize('filter-by-category', 'Filter by category')}
                    >
                        <span class="shrink-0 label-md font-cvi-medium text-cvi-fg-muted py-cvi-5 mr-cvi-4">
                            {localize('categories', 'Categories')}:
                        </span>
                        <div class="flex flex-wrap items-end gap-x-cvi-8 gap-y-cvi-2 flex-1 min-w-0">
                            <For each={tabnavItems()}>
                                {(item) => {
                                    const isActive = () => item.key === activeTabKey();
                                    return (
                                        <button
                                            type="button"
                                            role="tab"
                                            aria-selected={isActive()}
                                            onClick={() => handleTabChange(item.key)}
                                            class={clsx(
                                                'shrink-0 label-md font-cvi-medium bg-transparent border-0 cursor-pointer whitespace-nowrap',
                                                'py-cvi-5 border-b-2 -mb-px transition-colors duration-200 text-cvi-fg-default',
                                                isActive() ? 'border-cvi-border-interactive' : 'border-transparent',
                                            )}
                                        >
                                            {item.label}
                                        </button>
                                    );
                                }}
                            </For>
                        </div>
                    </div>
                    <div class="hidden tablet-original:block mb-cvi-8 border-b border-cvi-border-muted" />
                    <Grid
                        templateShorthand={[4, 4, 4]}
                        responsive={{ mobile: [12], tablet: [6, 6], largeDesktop: [4, 4, 4] }}
                        customCss='row-gap: var(--card-row-gap);'
                    >
                        <For each={filteredPosts()}>{(post: BlogNewsListCardData) => {
                            return createGroupedBlogAndNewsCard({post});
                        }}
                        </For>
                    </Grid>
                </div>
            </Section>
        </ErrorCatcher>
    );
};